import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { deusSocket } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';
import './AuthScreen.css'; // Reuse the AuthScreen CSS

const ResetPasswordScreen = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get userId and token from URL
  const userId = searchParams.get('userId');
  const token = searchParams.get('token');
  
  // Form state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  // Validate that we have the required params
  useEffect(() => {
    if (!userId || !token) {
      setError('Invalid password reset link. Please request a new one.');
    }
  }, [userId, token]);

  // Ensure the socket is connected
  const ensureSocketConnection = () => {
    if (!deusSocket.connected) {
      console.log('Socket disconnected, reconnecting...');
      deusSocket.connect();
      
      // Return a promise that resolves when the socket is connected
      return new Promise((resolve) => {
        const onConnect = () => {
          console.log('Socket reconnected');
          deusSocket.off('connect', onConnect);
          resolve();
        };
        
        deusSocket.on('connect', onConnect);
        
        // If already connected, resolve immediately
        if (deusSocket.connected) {
          deusSocket.off('connect', onConnect);
          resolve();
        }
      });
    }
    
    // If already connected, return a resolved promise
    return Promise.resolve();
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    // Basic validation
    if (!password || !confirmPassword) {
      setError('Please enter both password fields');
      setIsLoading(false);
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      setIsLoading(false);
      return;
    }
    
    // Ensure socket is connected before proceeding
    await ensureSocketConnection();
    
    // Setup event handlers
    const handleResetSuccess = () => {
      setIsLoading(false);
      setSuccess(true);
      
      // Remove event listeners
      deusSocket.off('deliverPasswordUpdateResponse', handleResetSuccess);
      deusSocket.off('error-event', handleResetError);
      
      // Redirect to login after a delay
      setTimeout(() => {
        navigate('/auth');
      }, 3000);
    };
    
    const handleResetError = (err) => {
      setIsLoading(false);
      setError(err.message || 'Failed to reset password. The link may have expired.');
      
      // Remove event listeners
      deusSocket.off('deliverPasswordUpdateResponse', handleResetSuccess);
      deusSocket.off('error-event', handleResetError);
    };
    
    // Set up socket listeners
    deusSocket.on('deliverPasswordUpdateResponse', handleResetSuccess);
    deusSocket.on('error-event', handleResetError);
    
    // Send the password reset request
    deusSocket.emit('updatePassword', { 
      user: userId,
      password,
      token
    });
  };

  if (success) {
    return (
      <div className="auth-screen-container">
        <div className="auth-screen-card">
          <div className="auth-success">
            <svg viewBox="0 0 24 24" className="auth-icon">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"></path>
            </svg>
            <h2>Password Reset Successful</h2>
            <p>Your password has been reset successfully. You can now log in with your new password.</p>
            <button 
              className="auth-button primary" 
              onClick={() => navigate('/auth')}
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-screen-container">
      <div className="auth-screen-card">
        {isLoading ? (
          <div className="auth-loading">
            <LoadingSpinner />
            <p>Resetting your password...</p>
          </div>
        ) : (
          <div className="auth-form-container">
            <h2>Reset Your Password</h2>
            {error && <div className="auth-error">{error}</div>}
            
            <form onSubmit={handlePasswordReset} className="auth-form">
              <div className="form-group">
                <label htmlFor="password">New Password</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  disabled={!userId || !token}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="confirm-password">Confirm Password</label>
                <input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  disabled={!userId || !token}
                />
              </div>
              
              <button 
                type="submit" 
                className="auth-button primary"
                disabled={!userId || !token || isLoading}
              >
                Reset Password
              </button>
              
              <button 
                type="button"
                className="auth-button secondary"
                onClick={() => navigate('/auth')}
              >
                Back to Login
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordScreen; 